// File: src/pages/payment-success.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from 'framer-motion';
import { CheckCircle, Loader2, Check, ArrowRight, Mail } from 'lucide-react';

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [paymentData, setPaymentData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkPayment = async () => {
      try {
        setLoading(true);
        
        // Paddle doesn't pass checkout ID in URL, so we check localStorage
        const checkoutId = localStorage.getItem('lastCheckoutId');
        
        if (checkoutId) {
          const paymentStatusUrl = `https://us-central1-asidorsmusic.cloudfunctions.net/getPaymentStatus?checkoutId=${checkoutId}`;
          const response = await fetch(paymentStatusUrl);

          if (response.ok) {
            const data = await response.json();
            setPaymentData(data);
          }
        }
        
        // Clear the stored checkout ID
        localStorage.removeItem('lastCheckoutId');
        setLoading(false);
      } catch (err) {
        console.error('Error checking payment:', err);
        setLoading(false);
      }
    };

    // Wait a moment for Paddle to process
    const timer = setTimeout(checkPayment, 1000);
    return () => clearTimeout(timer);
  }, []);

  const handleReturnHome = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen flex items-center justify-center flex-col p-4 sm:p-8 bg-[#08080a] text-white font-sans antialiased relative overflow-hidden">
      {/* Ambient Background Gold Glows */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[30rem] h-[30rem] bg-[#bd9f67]/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-md w-full relative z-10">
        {loading ? (
          /* Loading State */
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-[#121318]/80 backdrop-blur-xl border border-white/10 rounded-2xl p-8 sm:p-10 text-center shadow-2xl flex flex-col items-center justify-center"
          >
            <div className="p-4 rounded-2xl bg-[#bd9f67]/10 border border-[#bd9f67]/20 text-[#bd9f67] mb-4">
              <Loader2 size={36} className="animate-spin" />
            </div>
            <h3 className="text-xl font-bold text-white mb-1">
              Verifying Payment...
            </h3>
            <p className="text-xs text-white/50 tracking-wider uppercase">
              Please wait while we confirm your order
            </p>
          </motion.div>
        ) : (
          /* Success State */
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className="bg-[#121318]/90 backdrop-blur-2xl border border-white/10 rounded-2xl p-6 sm:p-8 shadow-2xl relative overflow-hidden"
          >
            {/* Corner Gold Accent Line */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#bd9f67] to-transparent" />

            {/* Success Icon */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 150, delay: 0.1 }}
              className="w-20 h-20 rounded-full bg-[#bd9f67]/15 border border-[#bd9f67]/40 text-[#bd9f67] flex items-center justify-center mx-auto mb-6 shadow-lg shadow-[#bd9f67]/10"
            >
              <CheckCircle size={44} strokeWidth={1.5} />
            </motion.div>

            {/* Title & Badge */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#bd9f67]/10 text-[#bd9f67] border border-[#bd9f67]/20 text-[10px] font-bold tracking-widest uppercase mb-3">
                 Transaction Complete
              </div>
              <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white mb-2">
                Payment Successful
              </h1>
              <p className="text-xs sm:text-sm text-white/60">
                Thank you for supporting our ministry!
              </p>
            </div>

            {/* Payment Details Card */}
            {paymentData && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-[#181a22] rounded-xl p-4 sm:p-5 mb-5 border border-white/5 space-y-3"
              >
                <p className="text-[10px] font-bold text-[#bd9f67] uppercase tracking-wider mb-1">
                  Receipt Summary
                </p>
                {paymentData.musicName && (
                  <div className="flex justify-between items-start text-xs border-b border-white/5 pb-2">
                    <span className="text-white/40 font-medium">Item</span>
                    <span className="text-white font-semibold text-right max-w-[180px] truncate">
                      {paymentData.musicName}
                    </span>
                  </div>
                )}
                {paymentData.price && (
                  <div className="flex justify-between items-center text-xs border-b border-white/5 pb-2">
                    <span className="text-white/40 font-medium">Amount Paid</span>
                    <span className="text-[#bd9f67] font-bold text-sm">
                      ${parseFloat(paymentData.price).toFixed(2)} USD
                    </span>
                  </div>
                )}
                {paymentData.email && (
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-white/40 font-medium">Account</span>
                    <span className="text-white/80 font-mono text-[11px] truncate max-w-[180px]">
                      {paymentData.email}
                    </span>
                  </div>
                )}
              </motion.div>
            )}

            {/* Info Steps Box */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-black/30 rounded-xl p-4 mb-6 border border-white/5 text-left"
            >
              <h3 className="text-xs font-semibold text-white/90 mb-3 uppercase tracking-wider flex items-center gap-1.5">
                <Mail size={14} className="text-[#bd9f67]" /> Next Steps
              </h3>
              <ul className="text-xs text-white/70 space-y-2.5">
                <li className="flex items-start gap-2.5">
                  <div className="p-0.5 rounded bg-[#bd9f67]/20 text-[#bd9f67] mt-0.5">
                    <Check size={12} />
                  </div>
                  <span>Check your email for download instructions</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <div className="p-0.5 rounded bg-[#bd9f67]/20 text-[#bd9f67] mt-0.5">
                    <Check size={12} />
                  </div>
                  <span>Your track is unlocked and added to your library</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <div className="p-0.5 rounded bg-[#bd9f67]/20 text-[#bd9f67] mt-0.5">
                    <Check size={12} />
                  </div>
                  <span>Re-download anytime from your account purchases</span>
                </li>
              </ul>
            </motion.div>

            {/* Return Button */}
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              onClick={handleReturnHome}
              className="w-full px-6 py-3.5 bg-[#bd9f67] hover:bg-[#d0b07a] text-gray-950 rounded-xl font-bold transition duration-200 shadow-lg shadow-[#bd9f67]/20 text-xs sm:text-sm uppercase tracking-wider flex items-center justify-center gap-2"
            >
              <span>Back to Home</span>
              <ArrowRight size={16} />
            </motion.button>

            {/* Support Link */}
            <p className="mt-5 text-center text-[11px] text-white/40">
              Need assistance?{' '}
              <a
                href="mailto:theasidorshome@yahoo.com"
                className="text-[#bd9f67] hover:underline font-medium"
              >
                Contact Support
              </a>
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
}